import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { getHospedajesColumns, hospedajesColumns, columnsExcel } from '@/pages/parametrizacion/hospedajes/utils/hospedajesColumns';
import ExportExcelButton from '@/components/ExportExcelButton';
import GestionarCamasModal from '@/pages/parametrizacion/hospedajes/components/GestionarCamasModal';
import GestionarBanosModal from '@/pages/parametrizacion/hospedajes/components/GestionarBanosModal';
import {
  fetchHospedajes,
  createHospedaje,
  updateHospedaje,
  deleteHospedaje,
  exportHospedajes,
  getCoordinadores,
  getEdificios,
  updateHospedajeCamas,
  updateHospedajeBanos
} from '@/pages/parametrizacion/hospedajes/Services/hospedajes.services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import FilterDrawer from '@/components/FilterDrawer';

const DEFAULT_FILTERS = {};

const HospedajesPage = () => {
  const { toast } = useToast();
  const [hospedajes, setHospedajes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [isCamasModalOpen, setIsCamasModalOpen] = useState(false);
  const [hospedajeForCamas, setHospedajeForCamas] = useState(null);

  const [isBanosModalOpen, setIsBanosModalOpen] = useState(false);
  const [hospedajeForBanos, setHospedajeForBanos] = useState(null);

  // Estados para datos de selects
  const [coordinadores, setCoordinadores] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [isLoadingSelects, setIsLoadingSelects] = useState(false);
  const [dynamicColumns, setDynamicColumns] = useState(hospedajesColumns);

  // Opciones estáticas para filtros
  const staticFilterOptions = useMemo(() => ({
    estado: [
      { value: 'Activo', label: 'Activo' },
      { value: 'Inactivo', label: 'Inactivo' },
    ],
    tipo: [
      { value: 'vertical', label: 'Vertical' },
      { value: 'horizontal', label: 'Horizontal' },
    ],
    gestionLenceria: [
      { value: 'Autonoma', label: 'Autónoma' },
      { value: 'Central', label: 'Central' },
      { value: 'No aplica', label: 'No aplica' },
    ],
    gestionAseo: [
      { value: 'Camarera Propietario', label: 'Camarera Propietario' },
      { value: 'camarera pagada por el propietario', label: 'Camarera pagada por el propietario' },
      { value: 'Directa', label: 'Directa' },
      { value: 'No aplica', label: 'No aplica' },
    ],
    tipoRenta: [
      { value: 'renta corta', label: 'Renta corta' },
      { value: 'mensual', label: 'Mensual' },
      { value: 'comercializable', label: 'Comercializable' },
      { value: 'casa', label: 'Casa' },
    ],
    tipoHabitacion: [
      { value: 'private_room', label: 'Habitación privada' },
      { value: 'entire_home', label: 'Casa completa' },
    ],
  }), []);

  // Columnas estables para filtros (sin funciones render)
  const filterColumns = useMemo(() => [
    { id: 'estado', label: 'Estado', type: 'select', options: staticFilterOptions.estado },
    { id: 'nombrePropiedad', label: 'Nombre de Activo', type: 'text' },
    { id: 'tipo', label: 'Tipo', type: 'select', options: staticFilterOptions.tipo },
    { id: 'coordinador', label: 'Coordinador', type: 'select', options: coordinadores },
    { id: 'gestionLenceria', label: 'Gestión de Lencería', type: 'select', options: staticFilterOptions.gestionLenceria },
    { id: 'gestionAseo', label: 'Gestión de Aseo', type: 'select', options: staticFilterOptions.gestionAseo },
    { id: 'tipoRenta', label: 'Tipo de Renta', type: 'select', options: staticFilterOptions.tipoRenta },
    { id: 'edificio', label: 'Edificio', type: 'select', options: edificios },
    { id: 'tipoHabitacion', label: 'Tipo de Habitación', type: 'select', options: staticFilterOptions.tipoHabitacion },
  ], [coordinadores, edificios, staticFilterOptions]);

  // Función para cargar datos de selects
  const loadSelectsData = useCallback(async () => {
    setIsLoadingSelects(true);
    try {
      const [coordinadoresResponse, edificiosResponse] = await Promise.all([
        getCoordinadores().catch(async (error) => {
          console.warn('Error en getCoordinadores, intentando fallback:', error);
          // Fallback: intentar obtener empleados y filtrar coordinadores
          try {
            const { fetchEmployees } = await import('@/pages/parametrizacion/employees/Services/employees.services');
            const employeesResponse = await fetchEmployees({ 
              limit: 500, 
              offset: 1, 
              query: { rol: 'coordinador', estado: 'Activo' } 
            });
            return employeesResponse;
          } catch (fallbackError) {
            console.error('Fallback también falló:', fallbackError);
            return { data: [] };
          }
        }),
        getEdificios().catch(async (error) => {
          console.warn('Error en getEdificios, intentando fallback:', error);
          // Fallback: intentar obtener edificios directamente
          try {
            const { fetchEdificios } = await import('@/pages/parametrizacion/edificios/Services/edificios.services');
            const edificiosResponse = await fetchEdificios({ 
              limit: 500, 
              offset: 1, 
              query: { estado: 'Activo' } 
            });
            return edificiosResponse;
          } catch (fallbackError) {
            console.error('Fallback edificios también falló:', fallbackError);
            return { data: [] };
          }
        })
      ]);


      let coordinadoresOptions = [];
      let edificiosOptions = [];

      if (coordinadoresResponse && coordinadoresResponse.data) {
        coordinadoresOptions = coordinadoresResponse.data.map(coordinador => ({
          value: coordinador._id || coordinador.id,
          label: `${coordinador.nombre}`,
          // Datos completos del coordinador para guardar
          fullData: {
            id: coordinador._id || coordinador.id,
            nombre: coordinador.nombre,
            correo: coordinador.correo || coordinador.email,
            cedula: coordinador.cedula || coordinador.documento
          }
        }));
        setCoordinadores(coordinadoresOptions);
      }

      if (edificiosResponse && edificiosResponse.data) {
        edificiosOptions = edificiosResponse.data.map(edificio => ({
          value: edificio._id || edificio.id,
          label: `${edificio.nombre} - ${edificio.ciudad}`,
          // Datos completos del edificio para guardar
          fullData: {
            id: edificio._id || edificio.id,
            nombre: edificio.nombre,
            ciudad: edificio.ciudad
          }
        }));
        setEdificios(edificiosOptions);
      }

      // Actualizar columnas dinámicas con los datos cargados
      setDynamicColumns(getHospedajesColumns(coordinadoresOptions, edificiosOptions));
      
      // Mostrar mensaje de éxito si se cargaron datos
      if (coordinadoresOptions.length > 0 || edificiosOptions.length > 0) {
        toast({
          title: "Datos cargados",
          description: `Se cargaron ${coordinadoresOptions.length} coordinadores y ${edificiosOptions.length} edificios.`,
        });
      }
    } catch (error) {
      console.error('Error al cargar datos de selects:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar algunos datos. Por favor, recarga la página.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSelects(false);
    }
  }, [toast]);

  // Transformar filtros para buscar en campos anidados
  const transformFiltersForQuery = useCallback((filters) => {
    const transformedFilters = { ...filters };
    
    // Si hay filtro de coordinador, buscar por coordinador.id
    if (transformedFilters.coordinador) {
      transformedFilters['coordinador.id'] = transformedFilters.coordinador;
      delete transformedFilters.coordinador;
    }
    
    // Si hay filtro de edificio, buscar por edificio.id
    if (transformedFilters.edificio) {
      transformedFilters['edificio.id'] = transformedFilters.edificio;
      delete transformedFilters.edificio;
    }
    
    return transformedFilters;
  }, []);

  const fetchAndSetHospedajes = useCallback(async () => {
    setIsLoading(true);
    try {
      // Transformar filtros antes de enviar
      const queryFilters = transformFiltersForQuery(filters);
      
      const response = await fetchHospedajes({
        limit: itemsPerPage,
        offset: currentPage,
        query: queryFilters,
      });

      if (response && response.data) {
        // Asegurar que siempre sea un array
        setHospedajes(Array.isArray(response.data) ? response.data : []);
        setTotalRecords(response.totalRecords || 0);
      } else {
        // Si no hay respuesta válida, establecer array vacío
        setHospedajes([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error al obtener hospedajes:', error);
      // En caso de error, establecer array vacío
      setHospedajes([]);
      setTotalRecords(0);
      toast({
        title: "Error",
        description: "No se pudieron cargar los hospedajes. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, toast, transformFiltersForQuery]);

  // Cargar datos iniciales
  useEffect(() => {
    loadSelectsData();
  }, [loadSelectsData]);

  useEffect(() => {
    fetchAndSetHospedajes();
  }, [fetchAndSetHospedajes]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, []);

  const handleRemoveFilter = useCallback((filterKey) => {
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters };
      delete newFilters[filterKey];
      return newFilters;
    });
    setCurrentPage(1);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleCreateHospedaje = () => {
    setCurrentItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditHospedaje = (hospedaje) => {
    // Transformar los objetos completos a IDs para el formulario
    const transformedItem = {
      ...hospedaje,
      coordinador: hospedaje.coordinador?.id || hospedaje.coordinador,
      edificio: hospedaje.edificio?.id || hospedaje.edificio
    };
    
    setCurrentItem(transformedItem);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDeleteHospedaje = (hospedaje) => {
    setItemToDelete(hospedaje);
    setIsDeleteDialogOpen(true);
  };

  // Función para transformar los datos antes de enviar al backend
  const transformFormData = (formData) => {    
    const transformedData = { ...formData };

    // Transformar coordinador de ID a objeto completo
    if (formData.coordinador) {
      const coordinadorSelected = coordinadores.find(c => c.value === formData.coordinador);
      if (coordinadorSelected && coordinadorSelected.fullData) {
        transformedData.coordinador = coordinadorSelected.fullData;
      } else {
        console.warn('No se pudo transformar el coordinador. Datos disponibles:', {
          formDataCoordinador: formData.coordinador,
          coordinadorEncontrado: coordinadorSelected
        });
      }
    } else {
      console.warn('No hay coordinador en formData');
    }

    // Transformar edificio de ID a objeto completo
    if (formData.edificio) {
      const edificioSelected = edificios.find(e => e.value === formData.edificio);
      if (edificioSelected && edificioSelected.fullData) {
        transformedData.edificio = edificioSelected.fullData;
      } else {
        console.warn('No se pudo transformar el edificio. Datos disponibles:', {
          formDataEdificio: formData.edificio,
          edificioEncontrado: edificioSelected
        });
      }
    } else {
      console.warn('No hay edificio en formData');
    }

    // Validar que los campos requeridos estén presentes
    if (!transformedData.coordinador) {
      console.error('ERROR: Coordinador faltante después de la transformación');
    }
    if (!transformedData.edificio) {
      console.error('ERROR: Edificio faltante después de la transformación');
    }

    return transformedData;
  };

  const handleModalSubmit = async (formData) => {
    try {
      // Transformar los datos antes de enviar
      const transformedData = transformFormData(formData);
      
      // Validar que los datos críticos estén presentes
      if (!transformedData.coordinador || !transformedData.coordinador.id) {
        toast({
          title: "Error de validación",
          description: "Debe seleccionar un coordinador válido.",
          variant: "destructive",
        });
        return;
      }

      if (!transformedData.edificio || !transformedData.edificio.id) {
        toast({
          title: "Error de validación", 
          description: "Debe seleccionar un edificio válido.",
          variant: "destructive",
        });
        return;
      }
      
      if (modalMode === 'create') {
        await createHospedaje(transformedData);
        toast({
          title: "Éxito",
          description: "Hospedaje creado correctamente.",
        });
      } else {
        await updateHospedaje(currentItem._id, transformedData);
        toast({
          title: "Éxito",
          description: "Hospedaje actualizado correctamente.",
        });
      }
      setIsModalOpen(false);
      fetchAndSetHospedajes();
    } catch (error) {
      console.error('Error al guardar hospedaje:', error);
      toast({
        title: "Error",
        description: `No se pudo ${modalMode === 'create' ? 'crear' : 'actualizar'} el hospedaje. Por favor, intenta de nuevo.`,
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteHospedaje(itemToDelete._id);
      toast({
        title: "Éxito",
        description: "Hospedaje eliminado correctamente.",
      });
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAndSetHospedajes();
    } catch (error) {
      console.error('Error al eliminar hospedaje:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el hospedaje. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleManageCamas = (item) => {
    setHospedajeForCamas(item);
    setIsCamasModalOpen(true);
  };

  const handleSaveCamas = async (hospedajeId, camas) => {
    try {
      await updateHospedajeCamas(hospedajeId, camas);
      await fetchAndSetHospedajes();
      toast({
        title: 'Éxito',
        description: 'Camas actualizadas correctamente.',
      });
    } catch (error) {
      console.error('Error al actualizar camas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar las camas.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleManageBanos = (item) => {
    setHospedajeForBanos(item);
    setIsBanosModalOpen(true);
  };

  const handleSaveBanos = async (hospedajeId, banos) => {
    try {
      await updateHospedajeBanos(hospedajeId, banos);
      await fetchAndSetHospedajes();
      toast({
        title: 'Éxito',
        description: 'Baños actualizados correctamente.',
      });
    } catch (error) {
      console.error('Error al actualizar baños:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar los baños.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleAction = (action, item) => {
    switch (action) {
      case 'manageCamas':
        handleManageCamas(item);
        break;
      case 'manageBanos':
        handleManageBanos(item);
        break;
      case 'edit':
        handleEditHospedaje(item);
        break;
      case 'delete':
        handleDeleteHospedaje(item);
        break;
      default:
        console.warn(`Acción no reconocida: ${action}`);
    }
  };

  // Preparar columnas para exportación
  const exportColumns = [
    { key: 'nombrePropiedad', header: 'Nombre del Activo' },
    { key: 'tipo', header: 'Tipo' },
    { key: 'coordinador.nombre', header: 'Coordinador' },
    { key: 'edificio.nombre', header: 'Edificio' },
    { key: 'edificio.ciudad', header: 'Ciudad' },
    { key: 'gestionLenceria', header: 'Gestión de Lencería' },
    { key: 'gestionAseo', header: 'Gestión de Aseo' },
    { key: 'tipoRenta', header: 'Tipo de Renta' },
    { key: 'numeroCamas', header: 'Número de Camas' },
    { key: 'numeroHabitaciones', header: 'Número de Habitaciones' },
    { key: 'tipoHabitacion', header: 'Tipo de Habitación' },
    { 
      key: 'camasParametrizadas', 
      header: 'Camas Parametrizadas',
      transform: (value, row) => {
        if (!row.camas || row.camas.length === 0) return 'Sin camas';
        return row.camas.map(c => `${c.nombre} (${c.cantidad})`).join(', ');
      }
    },
    { 
      key: 'banosParametrizados', 
      header: 'Baños Parametrizados',
      transform: (value, row) => {
        if (!row.banos || row.banos.length === 0) return 'Sin baños';
        return row.banos.map(b => `${b.nombre} (${b.cantidad})`).join(', ');
      }
    },
    { key: 'estado', header: 'Estado' },
  ];

  // Procesar datos para mostrar en la tabla
  const displayedData = useMemo(() => {
    if (!Array.isArray(hospedajes)) {
      return [];
    }
    return hospedajes.map(hospedaje => ({
      ...hospedaje,
      coordinadorDisplay: hospedaje?.coordinador?.nombre || '-',
      edificioDisplay: hospedaje?.edificio ? `${hospedaje.edificio.nombre} - ${hospedaje.edificio.ciudad}` : '-'
    }));
  }, [hospedajes]);

  return (
    <div className="container mx-auto ">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-2 mb-6">
          <Building className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Gestión de activos</h1>
            <p className="text-muted-foreground">
              Administra y configura los activos del sistema
            </p>
          </div>
        </div>
      </motion.div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activos</CardTitle>
              <CardDescription>
                Lista de todos los activos registrados en el sistema
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsDrawerOpen(true)}
                className="flex items-center space-x-2"
              >
                <FilterIcon className="h-4 w-4" />
                <span>Filtros</span>
              </Button>
              <ExportExcelButton
                service={exportHospedajes}
                filters={filters}
                columns={exportColumns}
                fileName="hospedajes"
              />
              <Button
                onClick={handleCreateHospedaje}
                className="flex items-center space-x-2"
                disabled={isLoadingSelects}
              >
                <PlusCircle className="h-4 w-4" />
                <span>Nuevo Activo</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoadingSelects && (
              <div className="text-sm text-foreground bg-muted/60 border border-border p-3 rounded-md shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>Cargando datos de coordinadores y edificios...</span>
                </div>
              </div>
            )}
            <AppliedFilters
              filters={filters}
              fields={filterColumns}
              onRemoveFilter={handleRemoveFilter}
            />
            
            <DataTable
              columns={dynamicColumns}
              data={displayedData}
              isLoading={isLoading}
              onAction={handleAction}
            />
            
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalRecords / itemsPerPage)}
              onPageChange={setCurrentPage}
              totalItems={totalRecords}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </CardContent>
      </Card>



      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSubmit}
        fields={dynamicColumns.filter(col => !col.actions && !col.hidden && !col.id.includes('Display'))}
        item={currentItem}
        mode={modalMode}
        title={modalMode === 'create' ? 'Crear Activo' : 'Editar Activo'}
      />

      <GestionarCamasModal
        isOpen={isCamasModalOpen}
        onClose={() => {
          setIsCamasModalOpen(false);
          setHospedajeForCamas(null);
        }}
        hospedaje={hospedajeForCamas}
        onSave={handleSaveCamas}
      />

      <GestionarBanosModal
        isOpen={isBanosModalOpen}
        onClose={() => {
          setIsBanosModalOpen(false);
          setHospedajeForBanos(null);
        }}
        hospedaje={hospedajeForBanos}
        onSave={handleSaveBanos}
      />

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        fields={filterColumns}
        onApplyFilters={handleFilterChange}
        initialFilters={filters}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el activo
              <strong> {itemToDelete?.nombrePropiedad}</strong> del sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HospedajesPage;
